import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import dayjs from 'dayjs';
import { HiMinus, HiPlus, HiRefresh } from 'react-icons/hi';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const ChartFuelLevel = ({ DataSource }) => {
    const [brushData, setBrushData] = useState(null);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [zoomRange, setZoomRange] = useState({ start: 0, end: 100 });

    // Kelompokkan data berdasarkan SensorCode yang unik
    const uniqueSensorCodes = useMemo(() => {
        return [...new Set(DataSource.map(item => item?.SensorCode).filter(Boolean))];
    }, [DataSource]);

    // Memoized data processing for better performance
    const data = useMemo(() => {
        // Group data by RefDate (waktu)
        const grouped = new Map();

        DataSource.forEach(item => {
            const refDateTime = dayjs.utc(item.RefDate).format('YYYY-MM-DD HH:mm:ss');
            const key = refDateTime;

            if (!grouped.has(key)) {
                grouped.set(key, {
                    refDateTime,
                });
            }
            const dataPoint = grouped.get(key);

            // Assign value untuk setiap SensorCode
            uniqueSensorCodes.forEach((sensorCode) => {
                if (item.SensorCode === sensorCode) {
                    dataPoint[`FuelLevel_${sensorCode}`] = item.Value1 ?? null;
                }
            });

            grouped.set(key, dataPoint);
        });

        return Array.from(grouped.values()).sort((a, b) => {
            return new Date(a.refDateTime) - new Date(b.refDateTime);
        });
    }, [DataSource, uniqueSensorCodes]);

    // Calculate display data based on zoom
    const displayData = useMemo(() => {
        if (brushData) {
            return data.slice(brushData.startIndex, brushData.endIndex + 1);
        }

        if (zoomLevel > 1) {
            const totalPoints = data.length;
            const visiblePoints = Math.floor(totalPoints / zoomLevel);
            const startIndex = Math.floor((zoomRange.start / 100) * (totalPoints - visiblePoints));
            const endIndex = startIndex + visiblePoints;
            return data.slice(startIndex, endIndex);
        }

        return data;
    }, [data, brushData, zoomLevel, zoomRange]);

    const handleZoomIn = () => {
        setZoomLevel(prev => Math.min(prev + 0.1, 5));
        setBrushData(null); // Clear brush selection when using button zoom
    };

    const handleZoomOut = () => {
        setZoomLevel(prev => Math.max(prev - 0.1, 1));
        setBrushData(null); // Clear brush selection when using button zoom
    };

    const handleResetZoom = () => {
        setZoomLevel(1);
        setZoomRange({ start: 0, end: 100 });
        setBrushData(null);
    };

    const handleZoomRangeChange = (start, end) => {
        setZoomRange({ start, end });
    };

    // Helper function untuk memberikan warna unik berdasarkan index
    const getColorForIndex = (index) => {
        const colors = ['#8884d8', '#1CA449', '#FA4125', '#82ca9d', '#FF8042', '#00C49F', '#FF6B6B', '#4ECDC4'];
        return colors[index % colors.length];
    };

    return (
        <div className="p-4 flex flex-col gap-4 w-full">
            <div className="flex gap-2 justify-end">
                <button
                    onClick={handleZoomOut}
                    className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                    title="Zoom Out"
                    disabled={zoomLevel <= 1}
                >
                    <HiMinus className="w-4 h-4" />
                </button>

                <span className="px-3 py-1 bg-gray-700 text-white rounded text-sm font-medium">
                    {Math.round(zoomLevel * 100)}%
                </span>

                <button
                    onClick={handleZoomIn}
                    className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                    title="Zoom In"
                    disabled={zoomLevel >= 5}
                >
                    <HiPlus className="w-4 h-4" />
                </button>

                <button
                    onClick={handleResetZoom}
                    className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                    title="Reset Zoom"
                >
                    <HiRefresh className="w-4 h-4" />
                </button>
            </div>

            <div className="flex justify-evenly lg:flex-row items-center gap-12 flex-col relative">
                <ResponsiveContainer width="100%" height={window.innerWidth > 768 ? 400 : 250}>
                    <LineChart data={displayData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis
                            dataKey="refDateTime"
                            tickFormatter={(value) => dayjs.utc(value).format('HH:mm:ss')}
                            stroke="#FFFFFF"
                            tick={{ fill: "#374151", fontSize: 12 }}
                            tickLine={{ stroke: "#374151" }}
                            axisLine={{ stroke: "#374151", strokeWidth: 2 }}
                            label={{ position: "bottom", offset: 0, style: { fill: "#374151", fontSize: 14, fontWeight: "bold" } }}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                        />
                        <YAxis
                            stroke="#374151"
                            tick={{ fill: "#374151", fontSize: 12 }}
                            tickLine={{ stroke: "#374151" }}
                            axisLine={{ stroke: "#374151", strokeWidth: 2 }}
                            domain={['auto', 'auto']}
                            label={{ angle: -90, position: "left", offset: 0, style: { fill: "#374151", fontSize: 14, fontWeight: "bold" } }}
                            width={80}
                            name={(() => {
                                return "Fuel Level (cm)";
                            })()}
                            tickFormatter={(value) => {
                                return `${value} cm`;
                            }}
                        />
                        <Tooltip
                            labelFormatter={(value) => {
                                const dataIndex = displayData.findIndex(item => item.refDateTime === value);
                                return dataIndex !== -1 ? displayData[dataIndex].refDateTime : '';
                            }}
                            formatter={(value, name) => {
                                if (value === null || value === undefined) return ['N/A', name];
                                const sensorCode = name.replace('FuelLevel_', '');
                                return [value + ' cm', sensorCode || 'Fuel Level'];
                            }}
                            labelStyle={{ color: "#FFFFFF" }}
                            itemStyle={{ color: "#FFFFFF" }}
                            contentStyle={{
                                backgroundColor: "#1e2939",
                                borderColor: "#4B5563",
                                borderRadius: "8px",
                                border: "1px solid #4B5563"
                            }}
                        />
                        {DataSource.length > 0 && uniqueSensorCodes.length > 0 && (
                            <Legend
                                wrapperStyle={{ color: "#FFFFFF" }}
                                iconType="line"
                                formatter={(value) => {
                                    const sensorCode = value.replace('FuelLevel_', '');
                                    return sensorCode ? `${sensorCode} (cm)` : 'Fuel Level (cm)';
                                }}
                            />
                        )}
                        {/* Render garis untuk setiap SensorCode yang ditemukan */}
                        {uniqueSensorCodes.map((sensorCode, index) => (
                            <Line
                                key={sensorCode}
                                type="monotone"
                                dataKey={`FuelLevel_${sensorCode}`}
                                stroke={getColorForIndex(index)}
                                strokeWidth={zoomLevel > 2 ? 3 : 2}
                                dot={{
                                    fill: getColorForIndex(index),
                                    strokeWidth: 2,
                                    r: zoomLevel > 2 ? 4 : 3,
                                    display: zoomLevel > 3 ? 'block' : 'none'
                                }}
                                activeDot={{
                                    r: zoomLevel > 2 ? 6 : 5,
                                    stroke: "#374151",
                                    strokeWidth: 1.5
                                }}
                                connectNulls={false}
                                animationDuration={300}
                                name={`FuelLevel_${sensorCode}`}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </div>

            {/* Zoom Range Slider (optional) */}
            {zoomLevel > 1 && (
                <div className="flex items-center gap-4 mt-4">
                    <span className="text-white text-sm">Zoom Range:</span>
                    <input
                        type="range"
                        min="0"
                        max="100"
                        value={zoomRange.start}
                        onChange={(e) => handleZoomRangeChange(parseInt(e.target.value), zoomRange.end)}
                        className="flex-1 h-2 bg-gray-400 rounded-lg appearance-none cursor-pointer"
                    />
                    <span className="text-white text-sm">{zoomRange.start}% - {zoomRange.end}%</span>
                </div>
            )}
        </div>
    )
}

export default ChartFuelLevel;
